import { useState, type DragEvent } from 'react';
import { Plus } from 'lucide-react';
import { ContextMenu } from '../ui/ContextMenu';
import { useImageUrl } from '../db/useImageUrl';
import { makeCover, removeItemImage, reorderGallery } from '../db/gallery';
import './Gallery.css';

/**
 * Every photo the item has, cover first, four across. Clicking one shows it
 * in the frame above rather than opening it — the strip drives the big view
 * the way a store page's does, and the big view is what expands.
 *
 * Reordering is HTML5 drag and drop rather than a library: the panel already
 * accepts dropped files, so the two have to share the same events anyway, and
 * they're told apart by whether the drag carries `Files`.
 */
export function Gallery({
  itemId,
  photos,
  coverImageId,
  selected,
  onSelect,
  onAdd,
}: {
  itemId: string;
  /** The cover, then the gallery. Index 0 is the cover when there is one. */
  photos: readonly string[];
  coverImageId: string | undefined;
  selected: number;
  onSelect: (index: number) => void;
  /** The panel owns adding — a drop, a paste and the + all report one way. */
  onAdd: () => void;
}) {
  const [dragging, setDragging] = useState<number | undefined>(undefined);
  const [over, setOver] = useState<number | undefined>(undefined);
  const [menu, setMenu] = useState<{ imageId: string; x: number; y: number } | undefined>(undefined);

  const isCover = (index: number) =>
    coverImageId !== undefined && photos[index] === coverImageId;

  /** The strip counts the cover; `reorderGallery` doesn't. */
  const inGallery = (index: number) => (coverImageId === undefined ? index : index - 1);

  function drop(to: number) {
    const from = dragging;
    setDragging(undefined);
    setOver(undefined);
    if (from === undefined || from === to) return;

    const moved = photos[from];
    if (moved === undefined) return;

    // Dropping onto the cover slot promotes, which is the same swap the
    // menu's Make Cover does — the outgoing cover lands where this one was.
    if (isCover(to)) {
      void makeCover(itemId, moved);
      return;
    }

    void reorderGallery(itemId, inGallery(from), inGallery(to));
  }

  return (
    <>
      <ul className="gallery">
        {photos.map((imageId, index) => (
          <li
            key={imageId}
            className={`gallery__cell${over === index ? ' gallery__cell--over' : ''}`}
            onDragOver={(event) => {
              if (dragging === undefined) return;
              event.preventDefault();
              setOver(index);
            }}
            onDrop={(event) => {
              if (dragging === undefined) return;
              event.preventDefault();
              // Stop the panel's file-drop handler seeing a reorder.
              event.stopPropagation();
              drop(index);
            }}
          >
            <Thumb
              imageId={imageId}
              position={index + 1}
              cover={isCover(index)}
              selected={selected === index}
              dragging={dragging === index}
              onSelect={() => onSelect(index)}
              // The cover has nowhere to be dragged to: everything else can
              // be promoted into its slot, but it can't demote itself.
              onDragStart={isCover(index) ? undefined : () => setDragging(index)}
              onDragEnd={() => {
                setDragging(undefined);
                setOver(undefined);
              }}
              onMenu={(x, y) => setMenu({ imageId, x, y })}
            />
          </li>
        ))}

        <li className="gallery__cell">
          <button type="button" className="gallery__add" aria-label="Add photos" onClick={onAdd}>
            <Plus size={20} aria-hidden />
          </button>
        </li>
      </ul>

      {menu !== undefined && (
        <ContextMenu x={menu.x} y={menu.y} label="Photo actions" onClose={() => setMenu(undefined)}>
          {(close) => (
            <>
              <button
                type="button"
                role="menuitem"
                className="menu__item"
                disabled={menu.imageId === coverImageId}
                onClick={() => {
                  close();
                  void makeCover(itemId, menu.imageId);
                }}
              >
                Make Cover
              </button>
              <button
                type="button"
                role="menuitem"
                className="menu__item"
                onClick={() => {
                  close();
                  void removeItemImage(itemId, menu.imageId);
                }}
              >
                Remove Photo
              </button>
            </>
          )}
        </ContextMenu>
      )}
    </>
  );
}

/** Lifted out so `useImageUrl` gets one hook per photo, keyed by the cell. */
function Thumb({
  imageId,
  position,
  cover,
  selected,
  dragging,
  onSelect,
  onDragStart,
  onDragEnd,
  onMenu,
}: {
  imageId: string;
  position: number;
  cover: boolean;
  selected: boolean;
  dragging: boolean;
  onSelect: () => void;
  /** Undefined where dragging isn't allowed — the cover. */
  onDragStart: (() => void) | undefined;
  onDragEnd: () => void;
  onMenu: (x: number, y: number) => void;
}) {
  const url = useImageUrl(imageId);

  const classes = [
    'gallery__thumb',
    selected ? 'gallery__thumb--selected' : '',
    dragging ? 'gallery__thumb--dragging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      aria-label={cover ? `Photo ${position}, the cover` : `Photo ${position}`}
      aria-current={selected ? 'true' : undefined}
      draggable={onDragStart !== undefined}
      onClick={onSelect}
      onDragStart={(event: DragEvent) => {
        // Firefox won't start a drag without payload, and this marks the drag
        // as ours rather than a file coming in from outside.
        event.dataTransfer.setData('text/plain', imageId);
        event.dataTransfer.effectAllowed = 'move';
        onDragStart?.();
      }}
      onDragEnd={onDragEnd}
      onContextMenu={(event) => {
        event.preventDefault();
        onMenu(event.clientX, event.clientY);
      }}
    >
      {url !== undefined && <img className="gallery__image" src={url} alt="" draggable={false} />}
    </button>
  );
}
